import _ from 'lodash'

export interface YardPositionDto {
  block?: string | null
  bay?: string | null
  row?: string | null
  tier?: number | null
}

export interface StowagePositionDto {
  bay?: number | null
  row?: number | null
  tier?: number | null
}

/**
 * Formats a stowage position into a string representation.
 *
 * The position is formatted as `bay.row.tier`, where each component is
 * zero-padded to a fixed width:
 * - `bay` is padded to 3 digits
 * - `row` is padded to 2 digits
 * - `tier` is padded to 2 digits
 *
 * @param position - The stowage position to format.
 * @returns The formatted stowage position string.
 */
export const formatPosition = (position: StowagePositionDto) => {
  const bay = (position.bay ?? 0).toString()
  const row = (position.row ?? 0).toString()
  const tier = (position.tier ?? 0).toString()

  return formatStringPosition(bay, row, tier)
}

/**
 * Formats a stowage position into a string representation.
 *
 * The position is formatted as `bay.row.tier`, where each component is
 * zero-padded to a fixed width:
 * - `bay` is padded to 3 digits
 * - `row` is padded to 2 digits
 * - `tier` is padded to 2 digits
 *
 * @returns The formatted stowage position string.
 */
export const formatStringPosition = (bay: string, row: string, tier: string) => {
  bay = bay.padStart(3, '0')
  row = row.padStart(2, '0')
  tier = tier.padStart(2, '0')

  return `${bay}.${row}.${tier}`
}

/**
 * Formats a given weight into a string representation.
 *
 * @param weight - The weight to format. Can be a number, null, or undefined.
 * @param returnZeroIfUnknown - If true, returns '0kg' when the weight is null or undefined.
 * @returns A string representing the formatted weight. Returns '0kg' if `returnZeroIfUnknown` is true and weight is null or undefined.
 *          Otherwise, returns the weight followed by 'kg' or 'Unknown' if the weight is null or undefined.
 *          If the weight is negative, returns '0kg'.
 */
export const formatWeight = (weight: number | null | undefined, returnZeroIfUnknown = false) => {
  if (weight == null) {
    return returnZeroIfUnknown ? '0kg' : 'Unknown'
  }

  return Math.max(0, weight) + 'kg'
}

export const parseLocationDto = (
  location: string | null | undefined,
): YardPositionDto | undefined => {
  if (!location) return undefined

  const dto = JSON.parse(location)
  if (!dto) return undefined

  dto.block ??= dto.Block
  dto.bay ??= dto.Bay
  dto.row ??= dto.Row
  dto.tier ??= dto.Tier

  if (!dto.block && !dto.bay && !dto.row && !dto.tier) return undefined

  return dto as YardPositionDto
}

export const toYardLocationString = (yardLocations: YardPositionDto[]) => {
  if (yardLocations.length === 0) return ''

  const sortedBays = _(yardLocations.map(yard => yard.bay))
    .uniq()
    .sort()
    .value()

  const formattedBay =
    sortedBays.length > 1 ? `${sortedBays[0]}-${sortedBays[sortedBays.length - 1]}` : sortedBays[0]
  const location = yardLocations[0]
  return `${location.block}.${formattedBay}.${location.row}.${location.tier}`
}
